import { makeStyles, mergeClasses, tokens, Text, Button, Tooltip } from '@coreai/fluentui-react';
import { DismissRegular, DismissCircleRegular } from '@fluentui/react-icons';
import { ConnectorIcon } from '@/components/ConnectionsView/ConnectorIcon';
import { DEFAULT_SELECTION_COLOR } from '@/utils/primitives';
import { ottoTokens } from '@/theme/ottoTokens';

/**
 * Get the appropriate text color based on background color brightness
 * Returns white for dark backgrounds, dark text for light backgrounds
 */
function getContrastTextColor(bgColor: string): string {
  // Remove # if present and parse hex
  const hex = bgColor.replace(/^#/, '');
  if (hex.length !== 6) return '#ffffff';

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (ITU-R BT.709)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Use white text for dark backgrounds (luminance < 0.5), dark text for light backgrounds
  return luminance < 0.5 ? '#ffffff' : '#242424';
}

const useStyles = makeStyles({
  // Base banner styles
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    minHeight: '36px',
    gap: tokens.spacingHorizontalS,
  },
  // Multi-selection: neutral background with blue left border
  multiBanner: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
  },
  // Workflow mode: subtle green left border, not loud background
  editBanner: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeft: `3px solid ${tokens.colorPaletteGreenBorder2}`,
  },
  workflowLabel: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  // Single node: same as multi (blue left border) for consistency
  singleBanner: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    flex: 1,
  },
  // Container for node pills that can wrap
  nodePillsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
    flexWrap: 'wrap',
    flex: 1,
    rowGap: tokens.spacingVerticalSNudge,
  },
  editingLabel: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  // Node pill with connector styling
  nodePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingLeft: '3px',
    paddingRight: tokens.spacingHorizontalSNudge,
    paddingTop: '3px',
    paddingBottom: '3px',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    maxWidth: '200px',
    boxShadow: tokens.shadow2,
  },
  pillIcon: {
    flexShrink: 0,
    borderRadius: tokens.borderRadiusSmall,
    overflow: 'hidden',
  },
  pillText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  pillDismiss: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: tokens.borderRadiusCircular,
    cursor: 'pointer',
    flexShrink: 0,
    marginLeft: tokens.spacingHorizontalXXS,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    '&:hover': {
      backgroundColor: ottoTokens.nodePillHoverOverlay,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  exitButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  editModeText: {
    color: tokens.colorNeutralForegroundOnBrand,
  },
});

/**
 * Info about a single node in the edit scope
 */
export interface NodeInfo {
  name: string;
  color: string;
  iconUri?: string;
}

/**
 * Represents the current edit scope (sticky - doesn't clear when clicking away)
 */
export interface EditScopeInfo {
  type: 'workflow' | 'single' | 'multi';
  /** Node names being edited (for single/multi) - legacy, use nodes instead */
  nodeNames?: string[];
  /** JSON path to the node in the workflow definition (for single node) */
  jsonPath?: string;
  /** Connector color to use for the banner (for single node) */
  color?: string;
  /** Icon URI for single node */
  iconUri?: string;
  /** Per-node info with individual colors (for multi) */
  nodes?: NodeInfo[];
}

interface ScopeBannerProps {
  /** The current edit scope (sticky state from store) */
  editScope: EditScopeInfo;
  /**
   * Callback to exit edit mode (return to full workflow mode)
   */
  onExitEditMode: () => void;
  /**
   * Callback to remove a specific node from the selection
   */
  onRemoveNode?: (nodeName: string) => void;
}

export const ScopeBanner = ({
  editScope,
  onExitEditMode,
  onRemoveNode,
}: ScopeBannerProps) => {
  const styles = useStyles();

  // Workflow mode - subtle green accent
  if (editScope.type === 'workflow') {
    return (
      <div className={mergeClasses(styles.banner, styles.editBanner)}>
        <div className={styles.content}>
          <Text className={styles.workflowLabel}>Editing: Workflow</Text>
        </div>
      </div>
    );
  }

  const nodeNames = editScope.nodeNames || [];
  const nodes = editScope.nodes || [];

  // Single node - use same pill style as multi for consistency
  if (editScope.type === 'single' || nodeNames.length === 1) {
    const node = nodes[0];
    const color = node?.color || editScope.color || DEFAULT_SELECTION_COLOR;
    const iconUri = node?.iconUri || editScope.iconUri;
    const name = node?.name || nodeNames[0] || 'Node';
    const textColor = getContrastTextColor(color);

    return (
      <div className={mergeClasses(styles.banner, styles.singleBanner)}>
        <Text className={styles.editingLabel}>
          Editing:
        </Text>
        <div className={styles.nodePillsContainer}>
          <span 
            className={styles.nodePill}
            style={{ backgroundColor: color, color: textColor }}
            title={name}
          >
            <div className={styles.pillIcon}>
              <ConnectorIcon {...(iconUri && { iconUri })} brandColor={color} size={20} />
            </div>
            <span className={styles.pillText}>{name}</span>
            <span 
              className={styles.pillDismiss}
              onClick={onExitEditMode}
              title="Deselect"
              style={{ color: textColor }}
            >
              <DismissRegular fontSize={12} />
            </span>
          </span>
        </div>
      </div>
    );
  }

  // Multi-node - pills with individual colors, icons, and dismiss buttons
  return (
    <div className={mergeClasses(styles.banner, styles.multiBanner)}>
      <Text className={styles.editingLabel}>
        Editing:
      </Text>
      <div className={styles.nodePillsContainer}>
        {nodes.length > 0 ? (
          // Use nodes with individual colors and icons
          nodes.map((node) => {
            const pillColor = node.color || DEFAULT_SELECTION_COLOR;
            const pillTextColor = getContrastTextColor(pillColor);
            return (
              <span 
                key={node.name} 
                className={styles.nodePill}
                style={{ 
                  backgroundColor: pillColor,
                  color: pillTextColor,
                }}
                title={node.name}
              >
                <div className={styles.pillIcon}>
                  <ConnectorIcon {...(node.iconUri && { iconUri: node.iconUri })} brandColor={pillColor} size={20} />
                </div>
                <span className={styles.pillText}>{node.name}</span>
                {onRemoveNode && (
                  <span 
                    className={styles.pillDismiss}
                    onClick={() => onRemoveNode(node.name)}
                    title={`Remove ${node.name} from selection`}
                    style={{ color: pillTextColor }}
                  >
                    <DismissRegular fontSize={12} />
                  </span>
                )}
              </span>
            );
          })
        ) : (
          // Fallback to nodeNames without individual colors
          nodeNames.map((name) => {
            const color = DEFAULT_SELECTION_COLOR;
            const textColor = getContrastTextColor(color);
            return (
              <span 
                key={name} 
                className={styles.nodePill}
                style={{ backgroundColor: color, color: textColor }}
                title={name}
              >
                <div className={styles.pillIcon}>
                  <ConnectorIcon brandColor={color} size={20} />
                </div>
                <span className={styles.pillText}>{name}</span>
                {onRemoveNode && (
                  <span 
                    className={styles.pillDismiss}
                    onClick={() => onRemoveNode(name)}
                    title={`Remove ${name} from selection`}
                    style={{ color: textColor }}
                  >
                    <DismissRegular fontSize={12} />
                  </span>
                )}
              </span>
            );
          })
        )}
      </div>
      <div className={styles.actions}>
        <Tooltip content="Clear all selections" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissCircleRegular />}
            onClick={onExitEditMode}
            className={styles.exitButton}
          />
        </Tooltip>
      </div>
    </div>
  );
};
